import { getConversationHistoryApi } from 'Pages/api/ai-companion/get-conversation-history/client.api';
import {
	AI_COMPANION_WARNING_TYPES,
	triggerAiCompanionUnsuccessfulMessageShownEvent,
} from 'Services/analytics/events/aiCompanionEvents';
import { logger } from 'Services/logger/logger';
import { SECOND, STALE_TURN_THRESHOLD } from 'Utilities/consts';
import { isE2ETestEnv } from 'Utilities/helpers';

import { ConversationStore } from './stores';

export const POLL_INTERVAL = isE2ETestEnv() ? 500 : 5 * SECOND;

let pollController: AbortController | null = null;

const wait = (ms: number, signal: AbortSignal) =>
	new Promise<void>((resolve) => {
		let timer: ReturnType<typeof setTimeout>;
		const onAbort = () => {
			clearTimeout(timer);
			resolve();
		};
		timer = setTimeout(() => {
			signal.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		signal.addEventListener('abort', onAbort, { once: true });
	});

export const cancelPendingPoll = () => {
	if (pollController) {
		pollController.abort();
		pollController = null;
	}
};

export const pollPendingTurn = async (
	conversationHistoryParams: Parameters<typeof getConversationHistoryApi>[0],
	createdAt: string,
) => {
	cancelPendingPoll();
	const controller = new AbortController();
	pollController = controller;

	const getRemainingTimeUntilStale = () => {
		const elapsed = Date.now() - new Date(createdAt).getTime();
		return Math.max(0, STALE_TURN_THRESHOLD - elapsed);
	};

	try {
		while (getRemainingTimeUntilStale() > POLL_INTERVAL) {
			await wait(POLL_INTERVAL, controller.signal);
			if (controller.signal.aborted) return;

			const response = await getConversationHistoryApi(conversationHistoryParams);
			if (controller.signal.aborted) return;
			ConversationStore.setState({ items: response.items });

			if (!response.pendingLatestTurnCreatedAt) {
				ConversationStore.setState({ thinking: false });
				return;
			}
		}

		// Not enough time for another poll interval — wait any remaining time and do a final fetch
		const remainingTime = getRemainingTimeUntilStale();
		if (remainingTime > 0) {
			await wait(remainingTime, controller.signal);
			if (controller.signal.aborted) return;
		}

		const finalResponse = await getConversationHistoryApi(conversationHistoryParams);
		if (controller.signal.aborted) return;
		ConversationStore.setState({ items: finalResponse.items });

		if (!finalResponse.pendingLatestTurnCreatedAt) {
			ConversationStore.setState({ thinking: false });
			return;
		}

		logger.warn({
			message: 'Pending turn poll exhausted',
			scope: 'AiCompanion:pendingTurnPoller',
		});
		triggerAiCompanionUnsuccessfulMessageShownEvent({ warningType: AI_COMPANION_WARNING_TYPES.TIMEOUT });

		ConversationStore.setState(({ items }) => ({
			items: [
				...items,
				{ createdAt: new Date().toISOString(), eventId: `agent-poll-timeout-${Date.now()}`, type: 'timeout' as const },
			],
			thinking: false,
		}));
	} catch (error) {
		if (controller.signal.aborted) return;
		logger.warn({
			error: error as Error,
			message: 'Pending turn poll failed',
			scope: 'AiCompanion:pendingTurnPoller',
		});
		ConversationStore.setState({ thinking: false });
	} finally {
		if (pollController === controller) {
			pollController = null;
		}
	}
};
